import { ChecklistItemDefinition } from '../../api/checklistItemDefinitions';
import { FEATURES } from '../../constants/features';

import { ChecklistItemIdentifier } from './identifiers';

/**
 * Checklist items that belong to a feature which can be switched off in the UI.
 *
 * The rows still exist in `Checklist_Item_Definitions` and any
 * `member_checklist_item` progress a user already recorded is left untouched —
 * these identifiers are only skipped while rendering, so flipping the flag in
 * `constants/features.ts` brings the items back exactly as they were.
 */
const FEATURE_GATED_IDENTIFIERS: ReadonlyArray<{
  identifier: ChecklistItemIdentifier;
  enabled: boolean;
}> = [
  { identifier: ChecklistItemIdentifier.BUY_WEDDING_BOOK, enabled: FEATURES.weddingBook },

  // Every one of these opens the Speechwriting lander (`/ceremony/:id/speechwriting`)
  // or its paid add-on, so they all hide together with the speechwriting feature.
  {
    identifier: ChecklistItemIdentifier.GET_SPEECHWRITING_HELP,
    enabled: FEATURES.speechwriting,
  },
  {
    identifier: ChecklistItemIdentifier.GET_PUBLIC_SPEAKING_HELP,
    enabled: FEATURES.speechwriting,
  },
  {
    identifier: ChecklistItemIdentifier.GUEST_GET_SPEECHWRITING_HELP,
    enabled: FEATURES.speechwriting,
  },
  {
    identifier: ChecklistItemIdentifier.GUEST_GET_PUBLIC_SPEAKING_HELP,
    enabled: FEATURES.speechwriting,
  },
];

const HIDDEN_CHECKLIST_ITEM_IDENTIFIERS: ReadonlySet<string> = new Set(
  FEATURE_GATED_IDENTIFIERS.filter((entry) => !entry.enabled).map(
    (entry) => entry.identifier
  )
);

/**
 * True when the checklist item may be rendered. Items belonging to a disabled
 * feature are hidden regardless of the status the member already has on them.
 */
export const isChecklistItemVisible = (identifier: string) =>
  !HIDDEN_CHECKLIST_ITEM_IDENTIFIERS.has(identifier);

/**
 * Drops feature-gated items from a checklist definition list.
 *
 * Apply this as early as possible — ideally where the definitions are fetched —
 * so hidden items are excluded from progress counts and "all complete" states
 * as well as from the rendered list. Relative order of the remaining items is
 * preserved.
 */
export const filterVisibleChecklistItems = <T extends ChecklistItemDefinition>(
  items: T[]
): T[] => items.filter((item) => isChecklistItemVisible(item.identifier));
